import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import SearchBox from '@replicon/react-search-box';
import { FormControl, FormHelperText } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useMessage, useMessages } from 'modules/intl';
import { useNotesSelectionReset } from 'modules/notes/list/hooks';
import { SearchCriteria } from 'modules/notes/common/components/SearchFacets';
import {
  getStartAndEndFromCriterions,
  getErrorInfoType,
  ErrorInfoType
} from 'modules/notes/common/components/SearchFacets/DateRangeFacet';
import { customLabelsTag } from 'modules/notes/common/components/SearchFacets/CustomLabelFacet/tag';
import { LabelValueType } from 'modules/notes/common/components/SearchFacets/LabelFacetDetails';
import { getScrollStyles } from 'modules/common/styles';
import { useFeatureFlag } from 'modules/common/hooks';
import {
  FilterContextSetter,
  useNotesFilterContext
} from './NotesFilterContextProvider';
import { useSearchFacets } from './hooks';

const useStyles = makeStyles((theme) => ({
  formControl: {
    display: 'flex',
    width: '100%'
  },
  container: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column'
  },
  offset: {
    marginTop: theme.spacing(2.5)
  }
}));

const useSearchBoxStyles = makeStyles((theme) => ({
  searchFacets: {
    overflow: 'auto',
    maxHeight: '41vh',
    ...getScrollStyles(theme)
  }
}));

type NotesSearchBoxProps = {
  hideDaterangeFacet?: boolean;
  className?: string;
};

const useConsolidateCustomLabelCriteria = ({
  searchCriteria,
  setFilterContext
}: {
  searchCriteria: SearchCriteria;
  setFilterContext: FilterContextSetter;
}) =>
  useEffect(() => {
    const customLabelsCriterions = (searchCriteria.criterions[
      customLabelsTag
    ] ?? []) as LabelValueType[];
    const clIds = customLabelsCriterions.map((l) => l.labelId);
    const hasDuplicateCl = clIds.some(
      (item, index, self) => self.indexOf(item) !== index
    );
    if (hasDuplicateCl) {
      const valuesMap = customLabelsCriterions.reduce((acc, val) => {
        if (acc[val.labelId] && acc[val.labelId].values) {
          acc[val.labelId].values!.push(...(val.values ?? []));
        } else {
          acc[val.labelId] = val;
        }
        return acc;
      }, {} as Record<string, LabelValueType>);
      const consolidatedValues = Object.values(valuesMap).map((val) => ({
        ...val,
        key: `${val.label}+${(val.values ?? []).map((v) => v.name).join('+')}`
      }));
      setFilterContext((prev) => ({
        ...prev,
        searchCriteria: {
          keywords: prev.searchCriteria.keywords,
          criterions: {
            ...prev.searchCriteria.criterions,
            [customLabelsTag]: consolidatedValues
          }
        }
      }));
    }
  }, [searchCriteria, setFilterContext]);

export const NotesSearchBox: React.FC<NotesSearchBoxProps> = ({
  hideDaterangeFacet = false,
  className
}: NotesSearchBoxProps) => {
  const {
    filterContext: { searchCriteria },
    setFilterContext
  } = useNotesFilterContext();

  const facets = useSearchFacets({ hideDaterangeFacet });

  const errorMessages = useMessages([
    'searchBox.error.dateValidation',
    'searchBox.error.invalidDateFormat',
    'searchBox.error.startDateOutOfRange',
    'searchBox.error.endDateOutOfRange'
  ]);
  const searchLabel = useMessage('search.title');
  const { resetForm } = useNotesSelectionReset();

  const [errorInfo, setErrorInfo] = useState<ErrorInfoType>({
    isError: false,
    message: undefined,
    isEndDateError: false,
    isStartDateError: false
  });
  const setSearchCriteria = useCallback(
    (searchCriteria: SearchCriteria) => {
      const errorInfo = getErrorInfoType(
        getStartAndEndFromCriterions(searchCriteria),
        errorMessages
      );
      setErrorInfo(errorInfo);
      if (!errorInfo.isError) {
        setFilterContext((previous) => ({
          ...previous,
          searchCriteria
        }));
        resetForm();
      }
    },
    [resetForm, setFilterContext, setErrorInfo, errorMessages]
  );

  useConsolidateCustomLabelCriteria({ searchCriteria, setFilterContext });

  const classes = useStyles();
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const searchMsg = useMessage('searchBox.searchMsg');

  const searchBoxClasses = useSearchBoxStyles();
  return (
    <div
      className={classNames(classes.container, className, {
        [classes.offset]: errorInfo.isError
      })}
    >
      <SearchBox
        classes={searchBoxClasses}
        searchHintLabel={searchLabel}
        facets={facets}
        searchCriteria={searchCriteria}
        setSearchCriteria={setSearchCriteria}
        searchIconAriaLabel={isTiwScreenReaderEnabled ? searchMsg : ''}
      />
      <FormControl error={errorInfo.isError} className={classes.formControl}>
        {errorInfo.isError && (
          <FormHelperText data-testid="search-box-error">
            {errorInfo.message}
          </FormHelperText>
        )}
      </FormControl>
    </div>
  );
};
export default NotesSearchBox;
